import React, { useContext, useEffect, useRef } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import ActionInlineButton from 'shared/lib/components/controls/buttons/actionInlineButton';
import IconArrowLeft from 'shared/icons/iconArrowLeft';
import IconEmptyFolder from 'shared/icons/iconEmptyFolder';
import { SelectItem } from 'shared/types/selectItem';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { useTranslation } from 'react-i18next';
import CustomSelect, {
  LabelProps,
} from '../../shared/lib/components/controls/selects/customSelect';
import { FolderSelectionVM } from 'widgets/selectPlacement/folderSelectionVM';
import IconCirclePlus from 'shared/icons/iconCirclePlus';
import { emptySelectItemId } from 'shared/lib/utils/globalUtils';
import CreateFolderController from 'widgets/folders/createFolderController';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { ExplorerEntityTypes } from 'entities/types';
import LocalLoader from 'shared/lib/components/progresses/localLoader';

export type PlacementSelectionProps = {
  labelProps?: LabelProps;
  initialSectionId: string | null;
  initialFolderId: string | null;
  onChangeFolder: (folderId: string | null) => void;
  onChangeSection: (sectionId: string) => void;
  filterFoldersById?: string[];
  afterFolderCreate?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
};

const PlacementSelection = <T extends string>({
  labelProps,
  initialSectionId,
  initialFolderId,
  onChangeFolder,
  onChangeSection,
  filterFoldersById,
  afterFolderCreate,
  onLoadingChange,
}: PlacementSelectionProps) => {
  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement | null>(null);

  const model = useLocalObservable(() => {
    return new FolderSelectionVM(
      {
        id: initialFolderId || initialSectionId,
        type: initialFolderId
          ? ExplorerEntityTypes.FOLDER
          : ExplorerEntityTypes.SECTION,
        sectionId: initialSectionId,
      },
      filterFoldersById
    );
  });

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(model.isFolderPending || model.isSectionPending);
    }
  }, [model.isFolderPending, model.isSectionPending, onLoadingChange]);

  const handleSectionChange = (newSectionId: string) => {
    model.selectSection(newSectionId);
    onChangeSection(newSectionId);
  };

  const clickTimeout = useRef<NodeJS.Timeout | null>(null);
  const CLICK_DELAY = 250;

  const handleFolderClick = (folder: SelectItem<T>, e: React.MouseEvent) => {
    e.stopPropagation();

    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current);
      clickTimeout.current = null;
      return;
    }

    clickTimeout.current = setTimeout(() => {
      clickTimeout.current = null;
      if (model.selectedFolderId === folder.id) {
        model.unselectCurrentFolder();
      } else {
        model.selectFolder(folder.id);
      }
    }, CLICK_DELAY);
  };

  const handleFolderDoubleClick = (folder: SelectItem<T>) => {
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current);
      clickTimeout.current = null;
    }
    model.selectFolder(folder.id);
    model.navigateToFolder(folder.id);
  };

  const _onChangeFolder = useRef(onChangeFolder);
  _onChangeFolder.current = onChangeFolder;
  const { selectedFolderId } = model;
  useEffect(() => {
    _onChangeFolder.current(selectedFolderId);
  }, [selectedFolderId]);

  const handleBackClick = () => {
    model.goBack();
  };

  const handleContainerClick = () => {
    if (model.selectedFolderId) {
      model.unselectCurrentFolder();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        target.closest('.click-outside-exception') ||
        (containerRef.current && containerRef.current.contains(target))
      ) {
        return;
      }

      if (model.selectedFolderId) {
        model.unselectCurrentFolder();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [model]);

  return (
    <Box
      ref={containerRef}
      onClick={handleContainerClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: sp.s40,
      }}
    >
      <CustomSelect
        value={model.section?.id || ''}
        items={model.sectionsAsItems}
        onChange={(newValue: string) => handleSectionChange(newValue)}
        label={labelProps?.textSection || t('common.selectSection')}
        labelProps={{
          sx: { color: (labelProps?.sx as any)?.color || 'text.primary' },
          typographyVariantSize: labelProps?.typographyVariantSize || 'f14',
        }}
      ></CustomSelect>
      {model.section && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: sp.s40,
              width: 1,
            }}
          >
            <Typography
              sx={{
                color: 'text.primary',
                ...labelProps?.sx,
              }}
              variant={labelProps?.typographyVariantSize || 'f14'}
            >
              {labelProps?.textFolder || t('common.selectFolder')}
            </Typography>

            <ActionInlineButton
              sizeVariant="small"
              Icon={IconArrowLeft}
              onClick={handleBackClick}
              disabled={!model.canNavigateUp}
            >
              {t('common.goUpLevel')}
            </ActionInlineButton>

            <List
              sx={{
                'border': `1px solid ${theme.palette.custom.borders}`,
                'height': 160,
                'overflowY': 'auto',
                'padding': 0,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(51, 51, 51, 0.15)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(51, 51, 51, 0.2)',
                  borderRadius: '10px',
                },
              }}
            >
              {model.isCreateFolder && model.section ? (
                <CreateFolderController
                  isOpen={model.isCreateFolder}
                  handleClose={() => {
                    model.isCreateFolder = false;
                  }}
                  parentId={model.parentId}
                  sectionId={model.section.id}
                  afterFolderCreate={(newFolderModel) => {
                    model.addFolder(newFolderModel);
                    afterFolderCreate && afterFolderCreate();
                  }}
                />
              ) : null}

              {model.foldersAsItems.map((folder) => (
                <ListItem
                  key={folder.id}
                  onClick={(e) => handleFolderClick(folder as SelectItem<T>, e)}
                  onDoubleClick={() =>
                    handleFolderDoubleClick(folder as SelectItem<T>)
                  }
                  className="noselect"
                  sx={{
                    '&:hover': {
                      backgroundColor:
                        model.selectedFolderId === folder.id ||
                        model.currentEntity.id === folder.id
                          ? theme.palette.custom.selected
                          : theme.palette.customBackgrounds.hoverEffect,
                    },
                    'backgroundColor':
                      model.selectedFolderId === folder.id ||
                      model.currentEntity.id === folder.id
                        ? theme.palette.custom.selected
                        : 'inherit',
                    'borderBottom': `1px solid rgba(51, 51, 51, 0.15)`,
                    'px': sp.s20,
                    'py': sp.s10,
                    'cursor': 'pointer',
                  }}
                >
                  <ListItemText
                    sx={{
                      fontSize: 'f16',
                      lineHeight: 1,
                    }}
                    primary={folder.name}
                  />
                </ListItem>
              ))}
              {model.foldersAsItems.length === 0 && !model.isCreateFolder && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: sp.s40,
                    height: 150,
                    justifyContent: 'center',
                  }}
                >
                  <IconEmptyFolder />
                  <Typography
                    variant="f14"
                    sx={{
                      textAlign: 'center',
                      color: theme.palette.custom.textInactive,
                    }}
                  >
                    В папке находятся только <br /> файлы или она пуста
                  </Typography>
                </Box>
              )}
            </List>
            <ActionInlineButton
              sizeVariant="small"
              disabled={
                !model.section?.id || model.section?.id === emptySelectItemId
              }
              Icon={IconCirclePlus}
              onClick={() => {
                model.isCreateFolder = true;
              }}
            >
              {t('noCategory.createNewFolder')}
            </ActionInlineButton>
            <Box
              sx={{ display: 'flex', gap: sp.s20, flexDirection: 'column' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="f16b">Выбрано:</Typography>
                <Typography variant="f16">
                  {model?.folder?.data?.name ||
                    model?.currentEntity.name ||
                    '--'}
                </Typography>
              </Box>
              <Typography variant="f12" color="text.secondary">
                После завершения действия файл будет располагаться в папке,
                указанной выше
              </Typography>
            </Box>
          </Box>
          <LocalLoader
            bgcolor="transparent"
            open={model.isFolderPending || model.isSectionPending}
          ></LocalLoader>
        </>
      )}
    </Box>
  );
};

export default observer(PlacementSelection);
